import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Flex,
  ModalBody,
  useToast,
  FormControl,
  FormLabel,
  Text,
  Input,
  Switch,
  Box,
} from '@chakra-ui/react';
import { SubmitButton } from '../shared/form/SubmitButton';
import { useTranslation } from 'react-i18next';
import {
  CreateUserRequest,
  Role,
  SlimUser,
  userApi,
} from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Controller, useForm } from 'react-hook-form';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ReactSelectOption } from '@texas/types';
import { ErrorLabel } from '../shared/ErrorLabel';
import { TexasSelect } from '../shared/form/TexasSelect';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { userEvents } from './events';
import { timezones } from '@texas/resources/countries/timezones';
import { SharedDisclosureProps } from '../shared/types';

export interface CreateExternalBrandUserProps extends SharedDisclosureProps {
  customerId: number;
  customerName: string;
  email?: string;
  name?: string;
  contactId?: number;
  onCreated?: (user: SlimUser) => void;
}

export function CreateExternalBrandUser({
  customerId,
  customerName,
  email,
  name,
  contactId,
  isOpen,
  onClose,
  onCreated,
}: CreateExternalBrandUserProps) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('user.createExternalBrandUser', { name: customerName })}
        </ModalHeader>
        <ModalCloseButton />
        <InnerModal
          onClose={onClose}
          customerId={customerId}
          email={email}
          name={name}
          onCreated={onCreated}
          contactId={contactId}
        />
      </ModalContent>
    </Modal>
  );
}

function InnerModal({
  onClose,
  customerId,
  email,
  name,
  contactId,
  onCreated,
}: {
  onClose: () => void;
  customerId: number;
  email?: string;
  name?: string;
  contactId?: number;
  onCreated?: (user: SlimUser) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation();

  const { request: createRequest, error } = useApiRequest(userApi.createUser);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<CreateUserRequest>({
    defaultValues: {
      role: Role.Brand,
      customerId: customerId,
      email,
      name,
      contactId,
      passwordLoginEnabled: true,
      rudholmIdentityLoginEnabled: false,
    },
  });

  const onSubmit = async (data: CreateUserRequest) =>
    await request(createRequest, [data], (user) => {
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      reset({
        role: Role.Brand,
        customerId: customerId,
        passwordLoginEnabled: true,
        rudholmIdentityLoginEnabled: false,
      });
      onClose();
      userEvents.newUserCreated.dispatch();
      if (onCreated) onCreated(user);
    });

  return (
    <>
      {error && <ErrorDetails error={error} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody as={Flex} gap={2} flexDir="column">
          <Flex gap={2}>
            <FormControl isInvalid={!!errors.email?.message}>
              <FormLabel>{t('general.email')}</FormLabel>
              <Input
                type="email"
                {...register('email', { required: true })}
                variant="texas"
              />
              <ErrorLabel text={errors.email?.message} />
            </FormControl>
            <FormControl isInvalid={!!errors.name?.message}>
              <FormLabel>{t('general.name')}</FormLabel>
              <Input
                {...register('name', { required: true })}
                variant="texas"
              />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
          </Flex>
          <Box>
            <FormControl pb={2}>
              <FormLabel>{t('general.timeZone')}</FormLabel>
              <Controller
                name="timeZone"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    value={timezones
                      .map((x) => ({
                        label: `${x.name} ${x.utc}`,
                        value: x.zone,
                      }))
                      .find((x) => x.value === field.value)}
                    onChange={(value) => field.onChange(value?.value ?? null)}
                    isClearable={true}
                    options={timezones.map<ReactSelectOption>((u) => ({
                      label: `${u.name} ${u.utc}`,
                      value: u.zone,
                    }))}
                  />
                )}
              />
            </FormControl>
            <Text fontSize="md" fontWeight="bold">
              {t('user.sendWelcomeEmail')}
            </Text>
            <Text fontSize="sm" color="gray.200" _light={{ color: 'gray.600' }}>
              {t('user.sendWelcomeMailDesc')}
            </Text>
            <FormControl display="flex" alignItems="center" pt={2} gap={2}>
              <Controller
                name="sendWelcomeMail"
                control={control}
                render={({ field }) => (
                  <Switch
                    isChecked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <FormLabel mb="0">{t('user.sendWelcomeMailToggle')}</FormLabel>
            </FormControl>
          </Box>
        </ModalBody>
        <ModalFooter
          bg="texas.bg.950"
          borderBottomRadius="md"
          gap={6}
          alignItems="end"
          _light={{ bg: 'gray.50' }}
        >
          <Flex w="fit-content" justify="flex-end">
            <SubmitButton disabled={!isValid} loading={isSubmitting}>
              {t('user.createUser')}
            </SubmitButton>
          </Flex>
        </ModalFooter>
      </form>
    </>
  );
}
